/** @format */

import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
  GitCompare,
  Hash,
  Loader,
  User,
} from "lucide-react";
import Header from "../../components/ui/Header";
import HeroSection from "../../components/ui/HeroSection";
import {
  getSopsWithMultipleVersions,
  type SopWithMultipleVersionsItem,
} from "../../api/sopsWithMultipleVersions";
import type { SopListFilters } from "../../store/useSopStore";
import { useOrganizationStore } from "../../store/useOrganizationStore";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import SOPListFilters, { countFilters } from "../../components/sop/SOPListFilters";
import "../../components/ui/Header.css";
import "../sop-dashboard/SopDashBoard.css";

const DEFAULT_LIMIT = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

function formatCreatedAt(isoString: string): string {
  const date = new Date(isoString.endsWith("Z") ? isoString : `${isoString}Z`);
  const locale = navigator.language;
  return (
    date.toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  );
}

interface TrackChangesRowProps {
  sop: SopWithMultipleVersionsItem;
  formatCreatedAt: (isoString: string) => string;
  isSuperAdmin?: boolean;
}

const TrackChangesRow: FC<TrackChangesRowProps> = ({
  sop,
  formatCreatedAt,
  isSuperAdmin = false,
}) => {
  const navigate = useNavigate();
  const dateStr = formatCreatedAt(sop.createdAt);
  const dateFull = (() => {
    const date = new Date(sop.createdAt.endsWith("Z") ? sop.createdAt : `${sop.createdAt}Z`);
    return date.toLocaleString(navigator.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });
  })();

  return (
    <div className="grid grid-cols-12 gap-4 p-4 hover:bg-gradient-to-r hover:from-slate-700/20 hover:to-slate-800/20 transition-all duration-200 group">
      {/* Title */}
      <div className={`flex items-center min-w-0 ${isSuperAdmin ? "col-span-4" : "col-span-6"}`}>
        <div className="flex items-center gap-3 w-full">
          <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full shrink-0" />
          <p className="text-white font-medium truncate">{sop.title || "Untitled"}</p>
        </div>
      </div>
      {/* Created by */}
      <div className="col-span-2 flex items-center">
        <span className="text-slate-300 text-sm truncate" title={sop.createdBy ?? undefined}>
          {sop.createdBy ?? "—"}
        </span>
      </div>
      {/* Organization - superadmin only */}
      {isSuperAdmin && (
        <div className="col-span-2 flex items-center">
          <span className="text-slate-300 text-sm truncate" title={sop.organizationName ?? undefined}>
            {sop.organizationName ?? "—"}
          </span>
        </div>
      )}
      {/* Versions count */}
      <div className="col-span-1 flex items-center justify-center">
        <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg px-3 py-1">
          <span className="text-cyan-300 font-semibold">{sop.versionCount}</span>
        </div>
      </div>
      {/* Created At */}
      <div className="col-span-2 flex items-center min-w-0">
        <span className="text-slate-300 text-sm whitespace-nowrap" title={dateFull}>
          {dateStr}
        </span>
      </div>
      {/* Actions */}
      <div className="col-span-1 flex items-center justify-center gap-2 min-w-[40px] shrink-0">
        <button
          type="button"
          onClick={() => navigate(`/sop/${sop.sopId}/versions`, { state: { from: "/track-changes" } })}
          className="flex items-center gap-2 rounded-lg border border-slate-600/50 bg-slate-700/30 px-3 py-2 text-sm text-slate-300 hover:bg-slate-600/40 hover:text-white transition-colors"
        >
          <GitCompare className="w-4 h-4 shrink-0 text-slate-400" />
        </button>
      </div>
    </div>
  );
};

const TrackChangesList: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const limitParam = parseInt(searchParams.get("limit") ?? String(DEFAULT_LIMIT), 10);
  const limit = [20, 50, 100].includes(limitParam)
    ? limitParam
    : DEFAULT_LIMIT;

  const { organizations } = useOrganizationStore();
  const { isSuperAdmin } = useSuperAdmin();
  const [appliedFilters, setAppliedFilters] = useState<SopListFilters>({});
  const [sops, setSops] = useState<SopWithMultipleVersionsItem[]>([]);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    hasNext: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtersForApi = useCallback(
    (f: SopListFilters): SopListFilters =>
      isSuperAdmin ? f : { ...f, organizationId: undefined },
    [isSuperAdmin],
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSopsWithMultipleVersions({
        page,
        limit,
        ...filtersForApi(appliedFilters),
      });
      setSops(res.sops);
      setPagination(res.pagination);
    } catch (err: unknown) {
      const message =
        err &&
          typeof err === "object" &&
          "response" in err &&
          err.response &&
          typeof (err.response as { data?: { message?: string } }).data?.message === "string"
          ? (err.response as { data: { message: string } }).data.message
          : err instanceof Error
            ? err.message
            : "Failed to load SOPs with multiple versions";
      setError(message);
      setSops([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, limit, appliedFilters, filtersForApi]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const appliedFiltersForList = isSuperAdmin
    ? appliedFilters
    : { ...appliedFilters, organizationId: undefined };

  const handleApplyFilters = useCallback(
    (filters: SopListFilters) => {
      setAppliedFilters(filters);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setAppliedFilters({});
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      return next;
    });
  }, [setSearchParams]);

  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.limit))
    : 1;
  const hasNext = pagination?.hasNext ?? false;

  const handlePrevPage = () => {
    if (page <= 1) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(page - 1));
      return next;
    });
  };

  const handleNextPage = () => {
    if (!hasNext) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(page + 1));
      return next;
    });
  };

  const handlePageSizeChange = (newLimit: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("limit", String(newLimit));
      next.set("page", "1");
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />

      <main className="pt-20 px-4">
        <div className="pt-8 w-full mx-auto">
          <div className="max-w-6xl mx-auto">
            <HeroSection
              title="Track Changes"
              description="SOPs with multiple versions — open one to compare versions."
              icons={[<GitCompare className="w-12 h-12 text-blue-400" />, null]}
            />

            <div className="bg-gradient-to-br from-gray-900/20 to-black/40 backdrop-blur-xl border border-gray-800/15 rounded-2xl p-8 shadow-2xl shadow-black/50">
              <div className="space-y-3 mb-4">
                <SOPListFilters
                  organizations={organizations}
                  onApply={handleApplyFilters}
                  onClear={handleClearFilters}
                  activeFilterCount={countFilters(appliedFiltersForList)}
                  disabled={loading}
                  showOrganizationFilter={isSuperAdmin}
                />
              </div>
              {error && (
                <div
                  className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300 flex items-center justify-between mb-4"
                  role="alert"
                >
                  <span>{error}</span>
                  <button
                    type="button"
                    onClick={() => fetchList()}
                    className="ml-2 px-2 py-1 rounded border border-red-400/50 hover:bg-red-500/20"
                  >
                    Retry
                  </button>
                </div>
              )}

              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="flex flex-col items-center gap-4">
                    <Loader className="animate-spin w-8 h-8 text-blue-400" />
                    <p className="text-slate-400">Loading SOPs...</p>
                  </div>
                </div>
              ) : (
                <div className="w-full">
                  {/* Table Header - match SopList grid layout */}
                  <div className="grid grid-cols-12 gap-4 p-4 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/30 rounded-t-xl">
                    <div className={`flex items-center gap-2 min-w-0 ${isSuperAdmin ? "col-span-4" : "col-span-6"}`}>
                      <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                      <span className="font-semibold text-white truncate">Title</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-amber-400" />
                      <span className="font-semibold text-white">Created by</span>
                    </div>
                    {isSuperAdmin && (
                      <div className="col-span-2 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-purple-400" />
                        <span className="font-semibold text-white">Organization</span>
                      </div>
                    )}
                    <div className="col-span-1 flex items-center justify-center gap-2">
                      <Hash className="w-4 h-4 text-cyan-400" />
                      <span className="font-semibold text-white">Versions</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-2 min-w-0">
                      <Calendar className="w-4 h-4 text-green-400 shrink-0" />
                      <span className="font-semibold text-white truncate">Created At</span>
                    </div>
                    <div className="col-span-1 flex items-center justify-center min-w-[40px] shrink-0">
                      <span className="font-semibold text-white">Actions</span>
                    </div>
                  </div>

                  {/* Table Body */}
                  <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 border-l border-r border-b border-slate-600/30 rounded-b-xl overflow-hidden">
                    {sops.length === 0 ? (
                      <div className="text-center py-16">
                        <div className="flex flex-col items-center gap-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-slate-700/50 to-slate-800/50 rounded-full flex items-center justify-center border border-slate-600/30">
                            <FileText className="w-8 h-8 text-slate-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white mb-2">
                              No SOPs with multiple versions
                            </h3>
                            <p className="text-slate-400 max-w-md">
                              No change history for your organization.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-700/30">
                        {sops.map((sop) => (
                          <TrackChangesRow
                            key={sop.sopId}
                            sop={sop}
                            formatCreatedAt={formatCreatedAt}
                            isSuperAdmin={isSuperAdmin}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {(pagination && (pagination.total > 0 || totalPages > 1)) && (
                    <div className="flex flex-wrap items-center justify-between gap-4 px-2 py-3 border-t border-slate-600/30 mt-4">
                      <div className="flex items-center gap-4 flex-wrap">
                        <p className="text-slate-400 text-sm">
                          Page {page} of {totalPages}
                          {pagination.total > 0 && (
                            <span className="ml-2">
                              ({pagination.total} total)
                            </span>
                          )}
                        </p>
                        <label className="flex items-center gap-2 text-slate-400 text-sm">
                          <span>Per page:</span>
                          <select
                            value={limit}
                            onChange={(e) =>
                              handlePageSizeChange(
                                Number(e.target.value) as 20 | 50 | 100,
                              )
                            }
                            className="rounded border border-slate-600/50 bg-slate-700/30 text-slate-200 px-2 py-1 text-sm"
                          >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handlePrevPage}
                          disabled={page <= 1}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={handleNextPage}
                          disabled={!hasNext}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TrackChangesList;
